/**
 * The user-facing vocabulary of the deposits page.
 *
 * ONE PLACE, BECAUSE THE WORDS CARRY THE MEANING
 * ----------------------------------------------
 * Wire values here are terse and occasionally misleading in German: an
 * `active_guarantee` is not "aktiv", it is *versichert*, and calling it active
 * would put it in the same sentence as a tenancy status it has nothing to do
 * with. Keeping every translation in one module means a badge, a filter option
 * and a mobile card can never disagree about what a row says.
 *
 * A LABEL IS NEVER INVENTED FOR A VALUE THE BACKEND DID NOT SEND
 * -------------------------------------------------------------
 * `labelFor` falls back to the raw value rather than to a friendly guess. An
 * unrecognized deposit type is a data-quality signal the backend already counts;
 * dressing it up as "Sonstige" would hide the thing the operator needs to fix.
 */

/* ─── Deposit types ──────────────────────────────────────────────────── */

export const TENANT_TYPE_LABELS = {
  cash: "Barkaution",
  insurance: "Versicherung",
  bank: "Bankdepot",
  none: "Keine",
};

/** Empty/NULL type. Distinct from the literal type "none", which is a recorded
 *  decision that there is no deposit — this is the absence of any answer. */
export const UNTYPED_LABEL = "Nicht zugeordnet";

export function depositTypeLabel(type) {
  const key = String(type || "").trim().toLowerCase();
  if (!key) return UNTYPED_LABEL;
  return TENANT_TYPE_LABELS[key] || type;
}

/* ─── Insurance providers ────────────────────────────────────────────── */

export const PROVIDER_LABELS = {
  swisscaution: "SwissCaution",
  smartcaution: "SmartCaution",
  firstcaution: "FirstCaution",
  gocaution: "goCaution",
  other: "Andere",
};

/**
 * The provider cell.
 *
 * Three different nothings, and they are not the same nothing: a bank deposit
 * has no provider by definition ("—"), an insurance policy without a recorded
 * insurer is a gap worth naming ("Nicht angegeben"), and a provider outside the
 * validated list is shown verbatim so the typo is visible.
 */
export function providerLabel(provider, depositType) {
  const type = String(depositType || "").trim().toLowerCase();
  if (type !== "insurance") return "—";
  const key = String(provider || "").trim().toLowerCase();
  if (!key) return "Nicht angegeben";
  return PROVIDER_LABELS[key] || provider;
}

/* ─── Security states ────────────────────────────────────────────────── */

/**
 * The six states `derive_tenant_security_state` returns, in German — the ONLY
 * place in this frontend that turns one into words.
 *
 * WHY THE TYPE IS AN ARGUMENT
 * ---------------------------
 * The backend keeps its states deliberately neutral: `held` says money is in
 * hand, `returned` says it went back. What an operator calls those depends on
 * what the security IS. Cash is "Erhalten" and comes back as "Zurückbezahlt";
 * a bank deposit is "Hinterlegt" and is "Freigegeben"; an insurance guarantee
 * is never received or repaid at all. One label per state would have to pick a
 * winner and be wrong for the other two, which is how "Zurückbezahlt" ended up
 * on guarantees nobody ever paid.
 *
 * So the mapping takes both, and the split lives here rather than in three
 * screens. Before K4x.2 it lived in three screens, and they disagreed.
 *
 * `tone` drives the badge colour, and the colour is always the SECOND signal:
 * every badge renders its label as text, so a reader who cannot distinguish the
 * tints loses nothing.
 */
const HELD_LABELS = { cash: "Erhalten" };
const RETURNED_LABELS = {
  cash: "Zurückbezahlt",
  insurance: "Freigegeben",
  bank: "Freigegeben",
};

const UNKNOWN_STATE = { label: "—", tone: "neutral" };

/**
 * `{ label, tone }` for one row's state, given its deposit type.
 *
 * An unknown state renders "—", never the raw value: a backend that grows a
 * seventh state should show a blank in the badge, not leak `some_new_state`
 * into the interface where it reads as a word nobody wrote.
 */
export function depositSecurityState(state, depositType) {
  const type = String(depositType || "").trim().toLowerCase();
  switch (state) {
    case "pending":
      // Type-independent: what is missing is the confirmation, not the money.
      return { label: "Ausstehend", tone: "warn" };
    case "active_guarantee":
      return { label: "Versichert", tone: "positive" };
    case "held":
      // Bank and anything else read as "Hinterlegt" — it claims custody, not a
      // payment, so it stays true for a type we cannot name.
      return { label: HELD_LABELS[type] || "Hinterlegt", tone: "positive" };
    case "returned":
      // Without a type, "Abgeschlossen" says the one thing we know: it is over.
      // "Zurückbezahlt" would invent a payment, "Freigegeben" a release.
      return { label: RETURNED_LABELS[type] || "Abgeschlossen", tone: "neutral" };
    case "unresolved":
      return { label: "Prüfen", tone: "warn" };
    case "excluded":
      return { label: "Nicht aktiv", tone: "muted" };
    default:
      return UNKNOWN_STATE;
  }
}

/** Label only, for callers that already have their own tone handling. */
export function securityStateLabel(state, depositType) {
  return depositSecurityState(state, depositType).label;
}

/** The three types a deposit can be filtered by, in the order they read. */
const FILTERABLE_TYPES = ["cash", "insurance", "bank"];

/**
 * How one state reads as a FILTER OPTION, where no single row's type applies.
 *
 * A row shows one word because it has one type. The option covers all of them
 * at once, so it has to name every word that state can produce: choosing
 * "Erhalten" would look like it excludes the bank deposits it in fact selects.
 *
 * Derived from the mapping above rather than typed out beside it — a
 * hand-written option list is the same duplication one level up, and it drifts
 * the day a label changes and nobody remembers the dropdown.
 */
export function securityStateFilterLabel(state) {
  const words = [];
  for (const type of FILTERABLE_TYPES) {
    const word = securityStateLabel(state, type);
    if (!words.includes(word)) words.push(word);
  }
  return words.join(" / ");
}

/* ─── Edit-form wording ──────────────────────────────────────────────── */

/**
 * What the two lifecycle flags are CALLED, per deposit type.
 *
 * Same reason the badge takes a type: nobody "receives" an insurance guarantee
 * and nobody "repays" one. The old tenancy form said "Kaution erhalten" and
 * "Kaution zurückgezahlt" for all three types, so a guarantee that was simply
 * unconfirmed read as a missing payment — the display half of that was K4x.2;
 * this is the edit half, from the same file so the form and the badge cannot
 * drift apart.
 *
 * `received`/`returned` are the WIRE names (`deposit_received`,
 * `deposit_returned`), deliberately, so a reader can map a label back to the
 * column it writes.
 */
const EDIT_WORDS = {
  insurance: {
    received: "Versicherung bestätigt",
    receivedAt: "Bestätigt am",
    returned: "Versicherung freigegeben",
    returnedAt: "Freigegeben am",
  },
  cash: {
    received: "Kaution erhalten",
    receivedAt: "Erhalten am",
    returned: "Kaution zurückbezahlt",
    returnedAt: "Zurückbezahlt am",
  },
  bank: {
    received: "Depot bestätigt",
    receivedAt: "Bestätigt am",
    returned: "Depot freigegeben",
    returnedAt: "Freigegeben am",
  },
};

/**
 * Neutral wording for a type we cannot name — including `none` and the empty
 * type a half-filled row carries. "Sicherheit" claims nothing about how the
 * money moves, which is the one thing an unknown type does not tell us.
 */
const EDIT_WORDS_FALLBACK = {
  received: "Sicherheit bestätigt",
  receivedAt: "Bestätigt am",
  returned: "Sicherheit freigegeben",
  returnedAt: "Freigegeben am",
};

/** `{received, receivedAt, returned, returnedAt}` labels for one deposit type. */
export function depositEditWords(depositType) {
  const type = String(depositType || "").trim().toLowerCase();
  return EDIT_WORDS[type] || EDIT_WORDS_FALLBACK;
}

/**
 * Whether a type carries an insurer at all.
 *
 * One predicate, used by the form to show the provider and premium fields and
 * by nothing else. It mirrors the backend's own rule — the two
 * `_clear_tenant_deposit_*_if_not_insurance_patch` validators — so hiding a
 * field and clearing it server-side can never disagree about which types count.
 */
export function depositTypeHasInsurer(depositType) {
  return String(depositType || "").trim().toLowerCase() === "insurance";
}

/* ─── Severity ───────────────────────────────────────────────────────── */

/**
 * The same three-level vocabulary the revenue data-quality section uses
 * (`bad` / `warn` / `info`), deliberately re-stated here rather than imported:
 * that module's map is bound to its own row shape and detail drawer, and
 * reaching into it would couple two unrelated surfaces for three strings.
 *
 * No score, no traffic light, no completeness percentage — a count and a
 * sentence, because that is what an operator can act on.
 */
export const SEVERITY_ORDER = ["bad", "warn", "info"];

export const SEVERITY_LABELS = {
  bad: "Kritisch",
  warn: "Prüfen",
  info: "Hinweis",
};
/* ─── Payout / Rückzahlungsdetails ───────────────────────────────────── */

/**
 * How a returned deposit was actually paid out.
 *
 * The wire values are the backend's `ALLOWED_DEPOSIT_RETURN_METHODS`
 * (`routes_admin_tenancies.py:37`) — anything else is a 422, so this map is the
 * complete set, not a subset the UI happens to offer. Order is the order the
 * select renders: the two that move money, then the two that do not.
 *
 * Lives here rather than beside the form because the legacy tenancy editor
 * already carries its own private copy of these four strings; a second form
 * with a third copy is how two surfaces start disagreeing about what
 * "Gemischt" means. K4x.3d folds the legacy copy into this one.
 */
export const DEPOSIT_RETURN_METHODS = [
  "cash",
  "bank_transfer",
  "mixed",
  "offset_only",
];

const RETURN_METHOD_LABELS = {
  cash: "Bar",
  bank_transfer: "Banküberweisung",
  mixed: "Gemischt",
  offset_only: "Vollständig verrechnet",
};

/** German label for one return method; the raw value if we cannot name it. */
export function depositReturnMethodLabel(method) {
  const key = String(method || "").trim().toLowerCase();
  if (!key) return "—";
  return RETURN_METHOD_LABELS[key] || method;
}

/**
 * Whether a method moves money to a bank account, and therefore needs an IBAN
 * and an account holder.
 *
 * This mirrors the backend invariant rather than inventing one: `cash` and
 * `offset_only` have their IBAN and holder nulled by
 * `_clear_deposit_dates_if_not_flagged_patch` regardless of what the client
 * sends, so showing those fields for them would offer an edit the server
 * discards. It is a visibility rule, NOT a validation rule — neither field is
 * ever required.
 */
export function depositMethodUsesBankAccount(method) {
  const key = String(method || "").trim().toLowerCase();
  return key === "bank_transfer" || key === "mixed";
}

/**
 * Whether a method fixes the returned amount at zero.
 *
 * `offset_only` means the deposit was settled entirely against claims, so
 * nothing was paid out. The backend forces `deposit_returned_amount` to 0.0 for
 * it; the form shows that rather than letting an operator type a number the
 * server will overwrite.
 */
export function depositMethodForcesZeroAmount(method) {
  return String(method || "").trim().toLowerCase() === "offset_only";
}
